import { FE_UPLT_HISTORY_TIMING_V2_250401 } from "@faire/retailer-settings/FE_UPLT_HISTORY_TIMING_V2_250401";
import { PageInitialized } from "@faire/retailer-visitor-shared/events";
import { getUser } from "@faire/retailer-visitor-shared/serialized-data/getUser";
import { useAssignEffect } from "@faire/retailer-visitor-shared/settings/useEffectAssignWhen";
import { useFrontendSetting } from "@faire/retailer-visitor-shared/settings/useFrontendSetting";
import { useSearchPageShowNoProductResults } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/useSearchPageShowNoProductResults";
import { useSearchPageFiltersIsLoading } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/Filters/useSearchPageFiltersIsLoading";
import { useSearchPageQueryDataUpdatedAt } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/Products/useSearchPageQueryDataUpdatedAt";
import { useSearchPageQueryIsError } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/Products/useSearchPageQueryIsError";
import { useSearchPageQueryIsLoading } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/Products/useSearchPageQueryIsLoading";
import { useSearchPageQueryQueryKey } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/Products/useSearchPageQueryKey";
import { useSearchPageIsFromDiscoverURL } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageIsFromDiscoverURL";
import { useSearchPageProductSearchString } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageProductSearchString";
import { useSearchPageActionName } from "@faire/retailer-visitor-shared/views/search-v2/util/useSearchPageActionName";
import { trackRetailersPageViewView } from "@faire/web-api--source/events/retailers/view/pageView";
import { markPageJsExecuted } from "@faire/web/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { ActionName } from "@faire/web/common/performance/userPerceivedLoadTiming/types";
import { useDefaultTrackResultCallback } from "@faire/web/common/performance/userPerceivedLoadTiming/useDefaultTrackResultCallback";
import { LoadTimingRef } from "@faire/web/common/performance/userPerceivedLoadTiming/UserPerceivedLoadTiming";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();

// Changes to UPLT tracking should also be made in SearchPagePerformanceTrackingWrapper.tsx and LoggedInSearchPageSDUIUPLTWithContext.tsx
export const useSearchPagePerformanceTracking = () => {
  const isFromDiscoverURL = useSearchPageIsFromDiscoverURL();
  const isProductsLoading = useSearchPageQueryIsLoading();
  const isFiltersLoading = useSearchPageFiltersIsLoading();
  const isLoading = isProductsLoading || isFiltersLoading;
  const showNoProductResults = useSearchPageShowNoProductResults();
  const isError = useSearchPageQueryIsError();
  const queryKey = useSearchPageQueryQueryKey();
  const dataUpdatedAt = useSearchPageQueryDataUpdatedAt();
  const queryString = useSearchPageProductSearchString();
  const { pathname } = useLocation();
  const userToken = getUser()?.token ?? "";

  const name: ActionName = useSearchPageActionName();
  const queryKeyString = JSON.stringify(queryKey);

  useAssignEffect(FE_UPLT_HISTORY_TIMING_V2_250401, true);
  const isSettingHistoryTimingV2 = useFrontendSetting(
    FE_UPLT_HISTORY_TIMING_V2_250401
  );

  useEffect(() => {
    if (!isLoading) {
      PageInitialized.publish();
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading) {
      trackRetailersPageViewView(
        "",
        "",
        queryString ?? "",
        isFromDiscoverURL ? "discover" : "search",
        pathname,
        name
      );
    }
  }, [isLoading, isFromDiscoverURL, queryString, pathname, name]);

  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: name,
    isPageLoading: isLoading && !isError,
    ignoreImages: showNoProductResults || isError,
    enableTrackPerformanceBreakdown: "internal-tracking",
    pageLoadKey: queryKeyString,
    enabled: true,
    dataUpdatedAt,
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
    enablePageUpdateFix: isSettingHistoryTimingV2 ? "v2" : undefined,
    trackResultCallback: useDefaultTrackResultCallback(userToken),
  }) as LoadTimingRef;

  useEffect(() => {
    if (isError) {
      loadTimingRef(null, {
        status: "error",
        error: "failed_to_load_required_data",
      });
    }
  }, [isError, loadTimingRef]);

  return loadTimingRef;
};
